import { PenIcon } from "@xola/icons";
import { FormGroup, Label, Tooltip } from "@xola/ui-kit";
import { FormField, SecondaryButton, SelectField } from "common";
import { Field, FieldArray, Formik, FormikProps } from "formik";
import { noop } from "lodash";
import React, { forwardRef, useEffect, useState } from "react";
import {
    KioskProductDisplayOrder,
    KioskProductPreferences,
    KioskProductPreferenceType,
    Product,
    ProductSelectionState,
} from "types";
import { kioskProductPreferenceDefaults } from "./KioskPreferenceDefaults";
import { MultipleProductSelectorModal } from "./MultipleProductSelectorModal";
import { SelectedProductList } from "./SelectedProductList";
import { ContentLayout } from "@/components/Layout/ContentLayout";

type KioskProductPreferencesFieldsProps = {
    initialValues: KioskProductPreferences;
};

export const KioskProductPreferencesFields = forwardRef<
    FormikProps<KioskProductPreferences>,
    KioskProductPreferencesFieldsProps
>(({ initialValues }, ref) => {
    const [showProductListModal, setShowProductListModal] = useState(false);
    const [kioskProductList, setKioskProductList] = useState<ProductSelectionState>({
        all: true,
        selected: kioskProductPreferenceDefaults[KioskProductPreferenceType.KIOSK_PRODUCT_LIST],
    });

    useEffect(() => {
        setKioskProductList({
            all: initialValues[KioskProductPreferenceType.KIOSK_PRODUCT_LIST].length === 0,
            selected: initialValues[KioskProductPreferenceType.KIOSK_PRODUCT_LIST] ?? [],
        });
    }, [initialValues]);

    // Handler to update kioskProductList from modal
    const handleProductListUpdate = (
        updatedProductList: Product[],
        setFieldValue: FormikProps<KioskProductPreferences>["setFieldValue"],
    ) => {
        const kioskProductListIds = updatedProductList.map((product) => product.id);
        setFieldValue(KioskProductPreferenceType.KIOSK_PRODUCT_LIST, kioskProductListIds);
        setKioskProductList({
            all: kioskProductListIds.length === 0,
            selected: kioskProductListIds,
        });
        setShowProductListModal(false);
    };

    return (
        <Formik enableReinitialize innerRef={ref} initialValues={initialValues} onSubmit={noop}>
            {({ values, setFieldValue }) => {
                return (
                    <ContentLayout.Section title="Products">
                        <FormField name={KioskProductPreferenceType.KIOSK_PRODUCT_DISPLAY} label="Display Products">
                            <SelectField
                                value={values[KioskProductPreferenceType.KIOSK_PRODUCT_DISPLAY]}
                                onChange={(event) =>
                                    setFieldValue(KioskProductPreferenceType.KIOSK_PRODUCT_DISPLAY, event.target.value)
                                }
                            >
                                <option value={KioskProductDisplayOrder.LIST}>List Layout</option>
                                <option value={KioskProductDisplayOrder.GRID}>Grid Layout</option>
                            </SelectField>
                        </FormField>

                        <div className="flex items-center">
                            <FormGroup className="w-full space-y-2">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <Label className="mb-1 text-sm font-bold text-black">Products to Display</Label>
                                        <Tooltip content="Select which products to display. If none are selected, the kiosk app will display all products in alphabetical order." />
                                    </div>
                                    <SecondaryButton size="small" onClick={() => setShowProductListModal(true)}>
                                        <PenIcon />
                                    </SecondaryButton>
                                </div>
                                <FieldArray name={KioskProductPreferenceType.KIOSK_PRODUCT_LIST}>
                                    {() => (
                                        <KioskProductSelector
                                            showProductListModal={showProductListModal}
                                            setShowProductListModal={setShowProductListModal}
                                            kioskProductList={kioskProductList}
                                            values={values}
                                            setFieldValue={setFieldValue}
                                            handleProductListUpdate={handleProductListUpdate}
                                        />
                                    )}
                                </FieldArray>
                            </FormGroup>
                        </div>
                    </ContentLayout.Section>
                );
            }}
        </Formik>
    );
});

// KioskProductSelector moved outside of KioskProductPreferencesFields
interface KioskProductSelectorProps {
    showProductListModal: boolean;
    setShowProductListModal: (show: boolean) => void;
    kioskProductList: ProductSelectionState;
    values: KioskProductPreferences;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
    handleProductListUpdate: (
        updatedProductList: Product[],
        setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
    ) => void;
}

const KioskProductSelector = ({
    showProductListModal,
    setShowProductListModal,
    kioskProductList,
    values,
    setFieldValue,
    handleProductListUpdate,
}: KioskProductSelectorProps) => {
    return (
        <div>
            <MultipleProductSelectorModal
                isOpen={showProductListModal}
                kioskProducts={values[KioskProductPreferenceType.KIOSK_PRODUCT_LIST] ?? []}
                onClose={() => setShowProductListModal(false)}
                onConfirm={(updatedProductList: Product[]) =>
                    handleProductListUpdate(updatedProductList, setFieldValue)
                }
            />
            <SelectedProductList selectedProducts={kioskProductList ?? { all: true, selected: [] }} />
            <FieldArray name={KioskProductPreferenceType.KIOSK_PRODUCT_LIST}>
                {() =>
                    values[KioskProductPreferenceType.KIOSK_PRODUCT_LIST]?.map((item, index) => (
                        <div key={item}>
                            <Field
                                name={`${KioskProductPreferenceType.KIOSK_PRODUCT_LIST}.${index}`}
                                className="hidden"
                            />
                        </div>
                    )) ?? null
                }
            </FieldArray>
        </div>
    );
};
